import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiService, FlowRunsByContact } from '../../services/api';
import { Screen } from '../../contexts/AppContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useFlowPermissions } from '../../hooks/useFlowPermissions';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';

/**
 * Home → Workflow runs by contact: who the workflows (and AI agents) talked to in the picked
 * range, most recent first — runs, still going, with errors, which flows. Tapping a contact opens
 * Flow runs filtered to them (stop, resume, continue from a step). Hidden without runs.
 */

const PAGE = 6;

export function FlowRunsByContactCard({ from, refreshToken, title, onNavigate }: { from: Date; refreshToken: number; title: (text: string, action?: { label: string; onPress: () => void }) => React.ReactNode; onNavigate: (screen: Screen, param?: string, routeParams?: Record<string, any>) => void }) {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const { canView } = useFlowPermissions();
  const [data, setData] = useState<FlowRunsByContact | null>(null);
  const [showAll, setShowAll] = useState(false);
  const fromKey = from.toISOString();

  useEffect(() => {
    if (!canView) return;
    let alive = true;
    apiService.getFlowRunsByContact(fromKey).then((res) => { if (alive) setData(res.statusCode === 1000 && res.data ? res.data : null); }).catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [fromKey, refreshToken, canView]);

  if (!canView || !data?.contacts.length) return null;
  const rows = showAll ? data.contacts : data.contacts.slice(0, PAGE);
  const time = (iso: string) => new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' } as any);

  return (
    <View style={styles.section}>
      {title(t('home.flowRuns.title', { count: data.totalRuns }), { label: t('home.flowRuns.all'), onPress: () => onNavigate('dashboard-flows-all') })}
      <View style={styles.list}>
        {rows.map((c, i) => (
          <TouchableOpacity key={c.contactId || i} style={[styles.row, { flexDirection: rtl.rowDirection }, i > 0 && styles.divider]} activeOpacity={0.8}
            onPress={() => onNavigate('dashboard-flows-all', undefined, { ref: c.contactId })}>
            <View style={styles.avatar}><Ionicons name="person-outline" size={14} color={colors.primary as string} /></View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={[styles.name, { textAlign: rtl.textStart }]} numberOfLines={1}>{c.contactName || c.contactPhone || t('home.flowRuns.unknown')}</Text>
              <Text style={[styles.meta, { textAlign: rtl.textStart }]} numberOfLines={1}>{c.flows.join(' · ')}</Text>
            </View>
            <View style={styles.right}>
              <Text style={styles.count}>{t('home.flowRuns.runs', { count: c.runs })}</Text>
              <View style={[styles.badges, { flexDirection: rtl.rowDirection }]}>
                {c.live > 0 && <Text style={styles.live}>{t('home.flowRuns.live', { count: c.live })}</Text>}
                {c.errors > 0 && <Text style={styles.errors}>{t('home.flowRuns.errors', { count: c.errors })}</Text>}
                {!c.live && !c.errors && <Text style={styles.meta}>{time(c.lastActivityAt)}</Text>}
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </View>
      {data.contacts.length > PAGE && (
        <TouchableOpacity onPress={() => setShowAll((v) => !v)}>
          <Text style={styles.more}>{showAll ? t('home.flowRuns.less') : t('home.flowRuns.more', { count: data.contacts.length - PAGE })}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const getStyles = () => StyleSheet.create({
  section: { gap: spacing.sm, marginBottom: spacing.lg },
  list: { borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  row: { alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.sm, paddingVertical: 10 },
  divider: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.border },
  avatar: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryTransparent },
  name: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  meta: { fontSize: 11, color: colors.text.secondary },
  right: { alignItems: 'flex-end', gap: 2 },
  count: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  badges: { gap: 6 },
  live: { fontSize: 11, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  errors: { fontSize: 11, color: colors.error },
  more: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.primary, textAlign: 'center' },
});
